/*
 * Copyright (c) 2025 - 2026. Bolmate B.V.
 */
import './style/vatSettings.scss';

import React, {KeyboardEventHandler, MouseEventHandler, useContext} from 'react';

import {useTranslation} from 'react-i18next';

import BolProduct from '../../../models/bolProductModel';

import {AwesomeIcon, ChangeFunction, getSetting, Link, Loader} from '@dvrd/dvr-controls';

import {CountryVATValues, translateVatValue} from '../../../util/vatUtil';

import {BolmateButton, BolmateInput} from '../../controls';

import {AppContext} from '../../misc/appContext';

import useRight from '../../misc/useRight';

import {AccessKey, BolmateSelectItem} from '../../../util/interfaces';

import CountryFlag from '../../misc/countryFlag';

import CommonPagination from '../../misc/commonPagination';

import {IconName} from '@fortawesome/fontawesome-svg-core';

import InfoBanner from '../../misc/infoBanner';

interface Props {
    onClickSync: MouseEventHandler;
    onClickImportExport: MouseEventHandler;
    onClickProduct: (product: BolProduct) => MouseEventHandler;
    onClickDefault: MouseEventHandler;
    onChangeSearch: ChangeFunction<string>;
    onEnterSearch: KeyboardEventHandler;
    onChangePage: (nextPage: number) => MouseEventHandler;
    onChangeLimit: ChangeFunction<number>;
    onHideProduct: (product: BolProduct) => MouseEventHandler;
    onUnHideProduct: (product: BolProduct) => MouseEventHandler;
    onToggleShowHidden: MouseEventHandler;
    products: Array<BolProduct>;
    total: number;
    search: string;
    defaultVATs: CountryVATValues;
    page: number;
    limit: number;
    isImporting: boolean;
    importingCount: number | null;
    loading: boolean;
    showHiddenProducts: boolean;
}

const LIMIT_ITEMS: Array<BolmateSelectItem> = [
    {label: 10, value: 10},
    {label: 20, value: 20},
    {label: 50, value: 50}
];

export default function VATSettings(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {account} = context.accountContext;
    const hasBolConnection = !!account?.getActiveAPIKey();
    const [canSync, canBulkUpload] = useRight(
        AccessKey.PRODUCT_SETTINGS_EDIT_TITLE,
        AccessKey.PRODUCT_SETTINGS_EDIT_VAT,
        AccessKey.PRODUCT_SETTINGS_SYNC,
        AccessKey.PRODUCT_SETTINGS_UPLOAD
    );

    const {
        onClickSync,
        onClickImportExport,
        onClickProduct,
        onChangeSearch,
        onEnterSearch,
        onChangePage,
        onChangeLimit,
        products,
        search,
        defaultVATs,
        page,
        limit,
        isImporting,
        importingCount,
        loading,
        onClickDefault,
        total,
        onHideProduct,
        onUnHideProduct,
        onToggleShowHidden,
        showHiddenProducts
    } = props;

    function renderTop() {
        const hideSyncForUsers: Array<string> = getSetting('hideProductSyncForUsers') ?? [];
        const hideSyncButton = (!!user && hideSyncForUsers.includes(user?.id)) || !canSync;
        const isSyncing = loading || isImporting;
        const syncLabel = isImporting
            ? importingCount
                ? t('settings.vat.fetchingCount', 'Bezig met ophalen van {{count}} producten', {count: importingCount})
                : t('settings.vat.fetching', 'Bezig met ophalen van producten')
            : t('settings.vat.syncWithBol', 'Sync met Bol');

        return (
            <div className='top-row'>
                <div className='left'>
                    {!hideSyncButton && (
                        <BolmateButton
                            onClick={onClickSync}
                            label={syncLabel}
                            disabled={isSyncing || !hasBolConnection}
                            secondary
                            ornaments={{
                                element: isSyncing ? (
                                    <AwesomeIcon name='rotate' className='spin' />
                                ) : (
                                    <AwesomeIcon name='rotate' />
                                )
                            }}
                        />
                    )}
                    <div className='search-container'>
                        <BolmateInput
                            onChange={onChangeSearch}
                            value={search}
                            onEnter={onEnterSearch}
                            label={t('settings.vat.search', 'Zoeken')}
                            placeholder={t('settings.vat.searchPlaceholder', 'Zoek een product')}
                            disabled={loading}
                            className='search-field'
                            autoFocus
                        />
                        {renderHiddenToggle()}
                    </div>
                </div>
                {renderDefaultBlock()}
            </div>
        );
    }

    function renderHiddenToggle() {
        let icon: IconName;
        let label: string;

        if (showHiddenProducts) {
            icon = 'eye';
            label = t('settings.vat.showActive', 'Toon enkel actieve producten');
        } else {
            icon = 'eye-slash';
            label = t('settings.vat.showHidden', 'Toon enkel verborgen & inactieve producten');
        }

        return (
            <Link onClick={onToggleShowHidden} className='hidden-products-toggle'>
                <AwesomeIcon name={icon} /> {label}
            </Link>
        );
    }

    function renderDefaultBlock() {
        return (
            <div className='default-block-col'>
                <div className='default-block'>
                    <div className='header-container'>
                        <label className='header-title'>
                            {t('settings.vat.defaultRates', 'Standaard BTW tarieven')}
                        </label>
                        <AwesomeIcon name='cog' onClick={onClickDefault} className='default-vat-cog' />
                    </div>
                    {renderCountryDefaults()}
                    {canBulkUpload && (
                        <Link onClick={onClickImportExport} className='upload-link'>
                            <AwesomeIcon name='file-excel' prefix='far' />{' '}
                            {t('settings.vat.importExport', 'Importeren & Exporteren...')}
                        </Link>
                    )}
                </div>
            </div>
        );
    }

    function renderCountryDefaults() {
        const {sortedCountryCodes} = defaultVATs;
        return <div className='country-defaults'>{sortedCountryCodes.map(renderCountryVAT(defaultVATs))}</div>;
    }

    function renderCountryVAT(values: CountryVATValues) {
        return function (countryCode: string, index: number) {
            return (
                <label key={index} className='country-vat' onClick={onClickDefault}>
                    <CountryFlag countryCode={countryCode} />
                    <span className='vat-value'>{translateVatValue(values.getCountryValue(countryCode))}</span>
                </label>
            );
        };
    }

    function renderProducts() {
        return (
            <div className='products-container'>
                <Loader active={loading} />
                <div className='row default-table-row head'>
                    <div />
                    <label>{t('settings.vat.colEan', 'EAN')}</label>
                    <label>{t('settings.vat.colName', 'NAAM')}</label>
                    <label>{t('settings.vat.colVat', 'BTW TARIEF')}</label>
                    <div />
                </div>
                {products.map(renderProduct)}
                {!products.length && (
                    <div className='default-table-row empty'>
                        <label className='empty-label'>
                            {hasBolConnection
                                ? t('settings.vat.emptyNoProducts', '- Geen producten gevonden -')
                                : t('settings.vat.emptyConnect', '- Maak eerst connectie met bol -')}
                        </label>
                    </div>
                )}
                <CommonPagination
                    onChangePage={onChangePage}
                    page={page}
                    total={total}
                    limit={limit}
                    onChangeLimit={onChangeLimit}
                    limitItems={LIMIT_ITEMS}
                    limitItemsPosition='top'
                    className='pagination'
                />
            </div>
        );
    }

    function renderProduct(product: BolProduct, index: number) {
        let hideIcon: IconName;
        let hideAction: MouseEventHandler;
        let hideTitle: string;

        if (product.hiddenByUser) {
            hideIcon = 'eye';
            hideAction = onUnHideProduct(product);
            hideTitle = t('settings.vat.unhideProductTitle', 'Product opnieuw tonen');
        } else {
            hideIcon = 'eye-slash';
            hideAction = onHideProduct(product);
            hideTitle = t('settings.vat.hideProductTitle', 'Product verbergen');
        }

        return (
            <div key={index} className='row default-table-row' onClick={onClickProduct(product)}>
                {renderProductImage(product.imageURL)}
                <label>{product.ean}</label>
                {renderProductTitle(product)}
                {renderProductVAT(product)}
                <div className='row-actions'>
                    <AwesomeIcon name={hideIcon} onClick={hideAction} className='hide-icon' title={hideTitle} />
                    <AwesomeIcon name='cog' />
                </div>
            </div>
        );
    }

    function renderProductImage(imageURL: string | null) {
        const image = imageURL ? (
            <img src={imageURL} className='product-image' alt='' />
        ) : (
            <AwesomeIcon name='image' className='image-icon' />
        );

        return <div className='product-image-container'>{image}</div>;
    }

    function renderProductTitle(product: BolProduct) {
        return (
            <label className='product-title'>
                {product.isNew && <span className='new-tag'>{t('settings.vat.new', 'Nieuw')}</span>}
                <span>{product.actualTitle}</span>
            </label>
        );
    }

    function renderProductVAT(product: BolProduct) {
        if (!product.vats.length) return <label>{t('settings.vat.default', 'Standaard')}</label>;
        return (
            <div className='product-country-vats'>
                {product.vats.sortedCountryCodes.map(renderCountryVAT(product.vats))}
            </div>
        );
    }

    return (
        <div className='vat-settings'>
            <InfoBanner
                featureKey='vat_settings'
                variant='info'
                title={t('settings.vat.bannerTitle', 'BTW instellingen per product')}
                showForNewUsersOnly={true}
            >
                {t(
                    'settings.vat.bannerBody',
                    'Geef aan welke producten een afwijkend BTW tarief per land hebben. Je kunt ook producten een simpelere naam geven.'
                )}
            </InfoBanner>

            <label className='settings-title'>{t('settings.vat.productSettings', 'Productinstellingen')}</label>
            {renderTop()}
            {renderProducts()}
        </div>
    );
}
